import { useMemo } from "react";
import { DemographicsSection } from "src/components/survey-results/demographics-list.component";
import { Table, TableBody, TableCaption, TableCell, TableRow } from "src/components/ui/table";
import { AnswerSource, DemographicsKey, QuestionStats, SurveyStatus } from "src/redux/survey/survey.types";
import { answerTitle, sortAnswers } from "../../view-survey/components/results-question.helpers";
import { orderBy } from "lodash";

interface DemographicsTableProps {
  readonly filter: DemographicsSection;
  readonly question: QuestionStats;
  readonly sourceFilter: AnswerSource;
  readonly surveyStatus?: SurveyStatus;
}

interface DemographicsTableCell {
  demographic: string;
  source: AnswerSource;
  percent: number;
  count: number;
}

const DemographicsTable = ({ question, filter, sourceFilter, surveyStatus }: DemographicsTableProps) => {
  const { All, AI, Human } = AnswerSource;
  const titleString = filter.title === 'DMA' ? 'Location' : filter.title;
  const showTable = filter?.values?.length > 0;
  const matchingDemoKey = filter.title.toLowerCase().replaceAll(' ', '_') as DemographicsKey;
  const showHumanStats = sourceFilter === All || sourceFilter === Human;
  const showAiStats = sourceFilter === All || sourceFilter === AI;

  const answers = useMemo(() => {
    return sortAnswers(question.answers, sourceFilter);
  }, [question, sourceFilter]);

  const rows = useMemo(() => {
    const tmpRows: DemographicsTableCell[][] = [];

    if (showTable && matchingDemoKey && question.demographics?.[matchingDemoKey]) {
      filter.values.forEach(val => {
        const humanRow: DemographicsTableCell[] = [];
        const aiRow: DemographicsTableCell[] = [];

        answers.forEach(answerStats => {
          const answerDemos = answerStats.demographics?.[matchingDemoKey];
          const valAnswerStats = answerDemos?.find(demo => demo.title === val);

          valAnswerStats && showHumanStats && humanRow.push({
            demographic: valAnswerStats.title,
            source: Human,
            percent: valAnswerStats.humanPercentage,
            count: valAnswerStats.humanUsers,
          });

          valAnswerStats && showAiStats && aiRow.push({
            demographic: valAnswerStats.title,
            source: AI,
            percent: valAnswerStats.aiPercentage,
            count: valAnswerStats.aiUsers,
          });
        });

        humanRow.length && tmpRows.push(humanRow);
        aiRow.length && tmpRows.push(aiRow);
      });
    }

    return tmpRows;
  }, [question, filter, sourceFilter, answers]);

  if (!showTable || !rows) {
    return null;
  }

  const renderTopRow = () => {
    return (
      <TableRow className="font-medium border-b-1 border-b-gray-light-10">
        <TableCell />
        {answers.map((answer, index) => (
          <TableCell
            key={answer.id}
            className="py-4"
          >
            {answerTitle(answer.title, index, surveyStatus)}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  const renderDataCell = (cell: DemographicsTableCell, index: number, nameKey: string, isTopAnswer: boolean) => {
    return (
      <TableCell
        className={isTopAnswer ? 'font-black' : 'font-medium'}
        key={`cell-${nameKey}-${index}`}
      >
        {`${Math.floor(cell.percent)}% (${cell.count})`}
      </TableCell>
    );
  };

  const renderDataRow = (data: DemographicsTableCell[]) => {
    const [{ demographic, source }] = data;
    const sourceString = source === Human ? 'Human' : 'AI';
    const rowName = `${demographic} (${sourceString})`;
    const nameKey = `${demographic}-${sourceString}`;
    const topAnswer = orderBy(data, 'percent', 'desc')[0];

    return (
      <TableRow
        className="border-b-gray-light-10 border-b-1"
        key={`row-${nameKey}`}
      >
        <TableCell className="text-sm font-medium align-top py-2">
          {rowName}
        </TableCell>
        {data.map((cell, index) => {
          return renderDataCell(cell, index, nameKey, cell === topAnswer);
        })}
      </TableRow>
    );
  };

  return (
    <div className="border-1 rounded-lg p-3 overflow-y-visible">
      <Table>
        <TableCaption className="text-base font-medium text-left border-b-1">
          {titleString}
        </TableCaption>
        <TableBody className="mt-6 [&>*:nth-child(even)]:bg-[#f6f6f6]">
          {renderTopRow()}
          {rows.map(renderDataRow)}
        </TableBody>
      </Table>
    </div>
  );
};

export default DemographicsTable;
